import { DateUtils } from '@shared/date-utils';
import { escapeHtml } from '@shared/utils';
import { ContextoPWA } from '@shared/types';
import {
  AcompanhamentoAluno,
  AcompanhamentoAlunoDisciplina,
  AcompanhamentoFormulario,
  AcompanhamentoOrdenacaoOption,
  AcompanhamentoPedagogicoRegistro,
  AcompanhamentoPeriodo
} from '../models/acompanhamento-pedagogico.types';

export function isDataFiltroValida(data: string | null | undefined): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(data || ''));
}

const ORDENACAO_ICONE_MAP: { [key: string]: string } = {
  'disciplina-asc': 'arrow-down-a-z',
  'disciplina-desc': 'arrow-down-z-a',
  'aluno-asc': 'arrow-down-a-z',
  'aluno-desc': 'arrow-down-z-a',
  'data-asc': 'arrow-up',
  'data-desc': 'arrow-down',
  'observacao-asc': 'arrow-down-a-z',
  'observacao-desc': 'arrow-down-z-a'
};

export function getOrdenacaoIcone(opcaoId: string): string {
  return ORDENACAO_ICONE_MAP[opcaoId] || 'arrow-up-down';
}

/** Ordena uma cópia de `registrosOriginais`; não muta o array recebido. */
export function aplicarOrdenacaoRegistros(
  registrosOriginais: AcompanhamentoPedagogicoRegistro[],
  ordenacaoSelecionada: AcompanhamentoOrdenacaoOption
): AcompanhamentoPedagogicoRegistro[] {
  const { campo, direcao } = ordenacaoSelecionada;
  const registrosFiltrados = [...registrosOriginais];

  registrosFiltrados.sort((a, b) => {
    let valorA = '';
    let valorB = '';

    if (campo === 'data') {
      valorA = a.data;
      valorB = b.data;
    } else if (campo === 'disciplina') {
      valorA = a.disciplina;
      valorB = b.disciplina;
    } else if (campo === 'aluno') {
      valorA = a.alunoNome;
      valorB = b.alunoNome;
    } else {
      valorA = a.observacao;
      valorB = b.observacao;
    }

    const comparacao = valorA.localeCompare(valorB, 'pt-BR', { numeric: true, sensitivity: 'base' });
    return direcao === 'asc' ? comparacao : comparacao * -1;
  });

  return registrosFiltrados;
}

/**
 * Preserva alunos já selecionados mesmo que saiam da lista recém-carregada
 * (ex: ao trocar o filtro "somente público-alvo").
 */
export function mesclarAlunosSelecionados(
  alunosCarregados: AcompanhamentoAluno[],
  alunosSelecionadosAtuais: AcompanhamentoAluno[]
): AcompanhamentoAluno[] {
  const alunosPorId = new Map<number, AcompanhamentoAluno>();

  alunosSelecionadosAtuais.forEach((aluno) => {
    alunosPorId.set(aluno.id, aluno);
  });

  alunosCarregados.forEach((aluno) => {
    alunosPorId.set(aluno.id, aluno);
  });

  return Array.from(alunosPorId.values());
}

export function getVinculoDisciplina(
  aluno: AcompanhamentoAluno,
  componenteId: number | null
): AcompanhamentoAlunoDisciplina | null {
  if (!componenteId) {
    return null;
  }

  return aluno.disciplinas.find((disciplina) => disciplina.componenteId === componenteId) || null;
}

export function isCampoAdicionalPreenchido(valor: unknown, formato?: string): boolean {
  if (valor === null || valor === undefined) {
    return false;
  }

  if (Array.isArray(valor)) {
    return valor.length > 0;
  }

  if (typeof valor === 'string') {
    return valor.trim().length > 0;
  }

  if (typeof valor === 'number' || typeof valor === 'boolean') {
    return true;
  }

  if (typeof valor === 'object') {
    // Valor em objeto: considera preenchido se ao menos uma chave tiver conteúdo.
    return Object.values(valor as Record<string, unknown>).some((item) =>
      isCampoAdicionalPreenchido(item, formato)
    );
  }

  return false;
}

export function getCamposObrigatoriosNaoPreenchidos(campoAdicional: unknown): string[] {
  if (!campoAdicional) {
    return [];
  }

  const configs = Array.isArray(campoAdicional)
    ? campoAdicional
    : (typeof campoAdicional === 'object' ? [campoAdicional] : []);

  const pendentes: string[] = [];

  configs.forEach((config: any) => {
    const agrupadores = Array.isArray(config?.agrupadores) ? config.agrupadores : [];
    agrupadores.forEach((agrupador: any) => {
      const campos = Array.isArray(agrupador?.campos) ? agrupador.campos : [];
      campos.forEach((campo: any) => {
        if (!campo?.obrigatorio) {
          return;
        }

        const preenchido = isCampoAdicionalPreenchido(campo?.valor, campo?.formato);
        if (!preenchido) {
          pendentes.push(campo?.titulo || campo?.variavel || 'campo obrigatório');
        }
      });
    });
  });

  return Array.from(new Set(pendentes));
}

/** Habilita o botão "Salvar" sem rodar a validação completa de `validarFormulario`. */
export interface CamposObrigatoriosPreenchidosContexto {
  formularioAberto: boolean;
  formulario: AcompanhamentoFormulario;
  carregandoAlunos: boolean;
  campoAdicional: unknown;
}

export function camposObrigatoriosPreenchidos(contexto: CamposObrigatoriosPreenchidosContexto): boolean {
  if (!contexto.formularioAberto) return false;
  if (!contexto.formulario.componenteId) return false;
  if (!contexto.formulario.data) return false;
  if (!contexto.formulario.alunoIds.length) return false;
  if (contexto.carregandoAlunos) return false;
  if (!contexto.formulario.observacao.trim()) return false;
  if (getCamposObrigatoriosNaoPreenchidos(contexto.campoAdicional).length > 0) return false;
  return true;
}

export interface ValidarFormularioContexto {
  formulario: AcompanhamentoFormulario;
  alunos: AcompanhamentoAluno[];
  limiteObservacao: number;
  carregandoCampoAdicional: boolean;
  campoAdicional: unknown;
}

export function validarFormulario(contexto: ValidarFormularioContexto): string[] {
  const { formulario, alunos, limiteObservacao, carregandoCampoAdicional, campoAdicional } = contexto;
  const erros: string[] = [];
  const hoje = DateUtils.formatarDataISO(new Date());

  if (!formulario.componenteId) {
    erros.push('Informe o componente curricular.');
  }

  if (!formulario.data) {
    erros.push('Informe a data do acompanhamento.');
  }

  if (!formulario.alunoIds.length) {
    erros.push('Selecione pelo menos um aluno ativo.');
  }

  if (!formulario.observacao.trim()) {
    erros.push('Informe a observação do acompanhamento.');
  }

  if (formulario.observacao.length > limiteObservacao) {
    erros.push('A observação deve ter no máximo 32.000 caracteres.');
  }

  if (formulario.data) {
    if (formulario.data > hoje) {
      erros.push('A data não pode ser maior que a data atual.');
    }
  }

  if (formulario.id && formulario.alunoIds.length > 1) {
    erros.push('Na edição, selecione apenas um aluno.');
  }

  formulario.alunoIds.forEach((alunoId) => {
    const aluno = alunos.find((item) => item.id === alunoId);
    if (!aluno) {
      return;
    }

    if (!aluno.ativo) {
      erros.push(`O aluno ${aluno.nome} não está ativo.`);
      return;
    }

    if (formulario.data && formulario.data < aluno.dataMatricula) {
      erros.push(`A data não pode ser menor que a data de matrícula do aluno ${aluno.nome} (${DateUtils.formatarDataBrasileira(aluno.dataMatricula)}).`);
    }

    if (formulario.componenteId) {
      const vinculo = getVinculoDisciplina(aluno, formulario.componenteId);

      if (vinculo) {
        if (vinculo.dispensado) {
          erros.push(`O aluno ${aluno.nome} tem o componente curricular dispensado.`);
          return;
        }

        if (formulario.data && formulario.data < vinculo.dataMatricula) {
          erros.push(`A data não pode ser menor que a data de matrícula do aluno ${aluno.nome} no componente curricular (${DateUtils.formatarDataBrasileira(vinculo.dataMatricula)}).`);
        }

        if (vinculo.dataFim && formulario.data && formulario.data > vinculo.dataFim) {
          erros.push(`A data não pode ser maior que a data de fim do componente curricular para o aluno ${aluno.nome} (${DateUtils.formatarDataBrasileira(vinculo.dataFim)}).`);
        }
      }
    }
  });

  if (formulario.id && carregandoCampoAdicional) {
    erros.push('Aguarde o carregamento dos dados adicionais para salvar.');
  }

  const camposObrigatoriosPendentes = getCamposObrigatoriosNaoPreenchidos(campoAdicional);
  if (camposObrigatoriosPendentes.length > 0) {
    const camposPreview = camposObrigatoriosPendentes.slice(0, 3).join(', ');
    const sufixo = camposObrigatoriosPendentes.length > 3 ? '...' : '';
    erros.push(`Preencha os campos obrigatórios dos dados adicionais: ${camposPreview}${sufixo}.`);
  }

  return Array.from(new Set(erros));
}

export function getFormularioInicial(): AcompanhamentoFormulario {
  return {
    id: null,
    componenteId: null,
    data: '',
    observacao: '',
    alunoIds: [],
    publicar: false,
    arquivos: [],
    campoAdicional: null
  };
}

/** Normaliza um formulário recebido, evitando mutação compartilhada de arrays. */
export function normalizarFormulario(formulario: AcompanhamentoFormulario): AcompanhamentoFormulario {
  return {
    ...formulario,
    publicar: !!formulario.publicar,
    alunoIds: [...formulario.alunoIds],
    arquivos: [...(formulario.arquivos ?? [])]
  };
}

/** Serializa campos do formulário pra detectar alterações não salvas (ignora arquivos/campoAdicional). */
export function serializarFormulario(formulario: AcompanhamentoFormulario): string {
  return JSON.stringify({
    id: formulario.id,
    componenteId: formulario.componenteId,
    data: formulario.data,
    observacao: formulario.observacao,
    publicar: !!formulario.publicar,
    alunoIds: [...formulario.alunoIds].sort((a, b) => a - b)
  });
}

export function temAlteracoesFormularioNaoSalvas(
  formularioAberto: boolean,
  formulario: AcompanhamentoFormulario,
  formularioSnapshotInicial: string
): boolean {
  if (!formularioAberto) {
    return false;
  }

  return serializarFormulario(formulario) !== formularioSnapshotInicial;
}

export function formatarDataHoraPublicacao(dataPublicacao: string): string {
  const data = new Date(dataPublicacao);
  const dia = String(data.getDate()).padStart(2, '0');
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  const ano = data.getFullYear();
  const hora = String(data.getHours()).padStart(2, '0');
  const minuto = String(data.getMinutes()).padStart(2, '0');

  return `${dia}/${mes}/${ano} às ${hora}:${minuto}h`;
}

export function getMensagemConfirmacaoDespublicacao(registro: AcompanhamentoPedagogicoRegistro): string {
  const aluno = escapeHtml(registro.alunoNomeSocial || registro.alunoNome || 'NOME');
  const dataRegistro = escapeHtml(DateUtils.formatarDataBrasileira(registro.data));
  const dataPublicacao = registro.dataPublicacao ? escapeHtml(formatarDataHoraPublicacao(registro.dataPublicacao)) : null;

  if (dataPublicacao) {
    return `O acompanhamento pedagógico do aluno(a) <strong>${aluno}</strong> do dia <strong>${dataRegistro}</strong> foi publicado em ${dataPublicacao}. Deseja desfazer a publicação?`;
  }

  return `O acompanhamento pedagógico do aluno(a) <strong>${aluno}</strong> do dia <strong>${dataRegistro}</strong> foi publicado. Deseja desfazer a publicação?`;
}

export function getMensagemConfirmacaoExclusao(registro: AcompanhamentoPedagogicoRegistro): string {
  const aluno = escapeHtml(registro.alunoNomeSocial || registro.alunoNome || 'NOME');
  const dataRegistro = escapeHtml(DateUtils.formatarDataBrasileira(registro.data));
  const dataPublicacao = registro.dataPublicacao ? escapeHtml(formatarDataHoraPublicacao(registro.dataPublicacao)) : null;

  if (registro.publicado && dataPublicacao) {
    return `O acompanhamento pedagógico do aluno(a) <strong>${aluno}</strong> do dia <strong>${dataRegistro}</strong> foi publicado em ${dataPublicacao}. Ao excluir esse acompanhamento, a publicação também será excluída. Deseja excluir esse acompanhamento?`;
  }

  if (registro.publicado) {
    return `O acompanhamento pedagógico do aluno(a) <strong>${aluno}</strong> do dia <strong>${dataRegistro}</strong> foi publicado. Ao excluir esse acompanhamento, a publicação também será excluída. Deseja excluir esse acompanhamento?`;
  }

  return `O acompanhamento pedagógico do aluno(a) <strong>${aluno}</strong> do dia <strong>${dataRegistro}</strong> será excluído. Deseja excluir esse acompanhamento?`;
}

export function getDescricaoPeriodoOption(periodo: AcompanhamentoPeriodo): string {
  if (periodo.id === 0) {
    return 'Período da atividade';
  }

  return periodo.descricao;
}

export function getContextoKey(contexto: ContextoPWA | null): string | null {
  if (!contexto?.turma?.id) {
    return null;
  }

  const componenteIds = (contexto.componentes || [])
    .map((componente) => componente?.id)
    .filter((id): id is number => typeof id === 'number')
    .sort((a, b) => a - b);

  return JSON.stringify({
    servidorId: contexto.servidor?.id || null,
    anoLetivoId: contexto.anoLetivo?.id || null,
    estabelecimentoId: contexto.estabelecimento?.id || null,
    turmaId: contexto.turma.id,
    entidadeId: contexto.entidade?.id || null,
    componenteIds
  });
}
